import { CoachingPracticeAudio } from '@euler/model/coaching-practice';
import { useServiceFactory } from '@euler/services/factory';
import { useCreation } from '@euler/utils/hooks/useCreation';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { BehaviorSubject } from 'rxjs';
import { lineVoiceOf } from './lineVoiceOf';
import { useLineReplay } from './useLineReplay';
import { LinePosition, LineVoice } from './useScenePlayback';
import { useTraineeReplay } from './useTraineeReplay';

/**
 * A finished practice's lines, heard again on tap as in the scene: a
 * customer line once voiced keeps its clips (a second tap does not voice
 * it again) and shows its waveform and length, and the line playing
 * ticks; a trainee line plays its stored voice. One voice at a time.
 */
export function useTranscriptPlayback(attemptId: string) {
  const { coachingService } = useServiceFactory();
  const voices$ = useCreation(
    () => new BehaviorSubject<ReadonlyMap<number, LineVoice>>(new Map()),
    [],
  );
  const position$ = useCreation(
    () => new BehaviorSubject<LinePosition | null>(null),
    [],
  );
  const heard = useRef(new Map<number, CoachingPracticeAudio[]>());
  const replay = useLineReplay(
    attemptId,
    useCallback((turn: number) => heard.current.get(turn), []),
    useCallback(
      // A line voiced only in part is voiced again on the next tap.
      (clips: CoachingPracticeAudio[]) => {
        if (!clips.length || !clips.every(x => x.audio)) return;
        const turn = clips[0].turn;
        heard.current.set(turn, clips);
        const voice = lineVoiceOf(clips);
        if (voice) voices$.next(new Map(voices$.getValue()).set(turn, voice));
      },
      [voices$],
    ),
  );
  // The trainee's stored voices, by voice id; either side quiets the other.
  const trainee = useTraineeReplay<string>(replay.stop);
  const playTrainee = useCallback(
    (voiceId: string) =>
      trainee.toggle(voiceId, async () => {
        const voice = await coachingService.practiceVoice(voiceId);
        return { mime: voice.mime, audio: voice.audio };
      }),
    [coachingService, trainee],
  );
  const play = useCallback(
    (turn: number) => {
      trainee.stop();
      return replay.play(turn);
    },
    [replay, trainee],
  );
  const stop = useCallback(() => {
    trainee.stop();
    replay.stop();
  }, [replay, trainee]);
  useEffect(() => {
    const ticks = replay.progress$.subscribe(({ clip, fraction }) => {
      if (!clip.durationMs) return;
      let ms = fraction * clip.durationMs;
      for (const x of heard.current.get(clip.turn) ?? [])
        if (x.seq < clip.seq) ms += x.durationMs ?? 0;
      position$.next({ turn: clip.turn, ms });
    });
    const stopped = replay.playing$.subscribe(turn => {
      if (turn == null) position$.next(null);
    });
    return () => {
      ticks.unsubscribe();
      stopped.unsubscribe();
    };
  }, [position$, replay]);
  return useMemo(
    () => ({
      playing$: replay.playing$,
      voices$,
      position$,
      play,
      /** The trainee line playing, by its stored voice's id. */
      traineePlaying$: trainee.playing$,
      playTrainee,
      stop,
    }),
    [play, playTrainee, position$, replay, stop, trainee, voices$],
  );
}
